import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { toast } from "sonner";
import { BookOpen, Banknote, TrendingUp, ShoppingBag, Users, Package, PieChart, Calendar, Plus, Trash2, X, FileText, Scale, Download, Upload } from "lucide-react";

const TABS = [
  { k: "cash-book",         label: "Cash Book",         icon: Banknote },
  { k: "bank-book",         label: "Bank Book",         icon: BookOpen },
  { k: "sales-register",    label: "Sales Register",    icon: TrendingUp },
  { k: "purchase-register", label: "Purchase Register", icon: ShoppingBag },
  { k: "debtors",           label: "Debtors (A/R)",     icon: Users },
  { k: "creditors",         label: "Creditors (A/P)",   icon: Package },
  { k: "journal",           label: "Journal",           icon: FileText },
  { k: "trial-balance",     label: "Trial Balance",     icon: Scale },
  { k: "balance-sheet",     label: "Balance Sheet",     icon: PieChart },
  { k: "pnl",               label: "P & L",             icon: TrendingUp },
  { k: "gstr1",             label: "GSTR-1",            icon: Download },
  { k: "tally-export",      label: "Tally Prime",       icon: Upload },
];

const today = () => new Date().toISOString().slice(0, 10);

export default function AdminLedgers() {
  const [tab, setTab] = useState("cash-book");
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPurchase, setShowPurchase] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      let path;
      if (["debtors", "creditors"].includes(tab)) {
        path = `/admin/ledgers/${tab}`;
      } else if (tab === "balance-sheet") {
        path = `/admin/ledgers/balance-sheet?as_of=${to}`;
      } else if (tab === "gstr1") {
        const month = (to || today()).slice(0, 7);
        path = `/admin/ledgers/gstr1?month=${month}`;
      } else if (tab === "tally-export") {
        path = `/admin/ledgers/tally-export/preview?until=${to || today()}`;
      } else {
        path = `/admin/ledgers/${tab}?from_=${from}&to=${to}`;
      }
      const r = await api.get(path);
      setData(r.data);
    } catch (e) { toast.error(apiError(e)); setData(null); }
    finally { setLoading(false); }
  };
  // Reset data synchronously on tab change so the previous tab's shape
  // never leaks into the new tab's renderer (crash guard).
  useEffect(() => { setData(null); load(); }, [tab, from, to]);   // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto" data-testid="admin-ledgers">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">// Books of accounts</div>
          <div className="font-display text-3xl font-black uppercase mt-1 flex items-center gap-2">
            <BookOpen size={26} /> Accounting Ledgers
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {tab !== "balance-sheet" && (
            <>
              <div className="flex items-center gap-2 border border-white/15 px-3 py-2">
                <Calendar size={14} className="text-white/50" />
                <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                  data-testid="ledger-from" className="bg-transparent text-sm outline-none" />
                <span className="text-white/40">→</span>
                <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                  data-testid="ledger-to" className="bg-transparent text-sm outline-none" />
              </div>
              {["cash-book", "bank-book", "sales-register", "purchase-register"].includes(tab) && (
                <QuickRange onPick={(f, t) => { setFrom(f); setTo(t); }} />
              )}
            </>
          )}
          {tab === "balance-sheet" && (
            <div className="flex items-center gap-2 border border-white/15 px-3 py-2">
              <Calendar size={14} className="text-white/50" />
              <span className="text-[10px] font-mono text-white/50 uppercase">As of</span>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                data-testid="ledger-asof" className="bg-transparent text-sm outline-none" />
            </div>
          )}
          {tab === "purchase-register" && (
            <button onClick={() => setShowPurchase(true)}
              data-testid="new-purchase-btn"
              className="bg-brand-orange text-black px-4 py-2 text-xs uppercase font-bold tracking-widest inline-flex items-center gap-1.5">
              <Plus size={12} /> New Purchase
            </button>
          )}
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-white/10 mb-4 overflow-x-auto">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.k} onClick={() => { setData(null); setTab(t.k); }}
              data-testid={`ledger-tab-${t.k}`}
              className={`px-4 py-3 text-[11px] uppercase font-mono tracking-widest transition inline-flex items-center gap-1.5 whitespace-nowrap ${tab === t.k ? "border-b-2 border-brand-orange text-white" : "text-white/50 hover:text-white"}`}>
              <Icon size={12} /> {t.label}
            </button>
          );
        })}
      </div>

      {loading && <div className="text-center py-16 text-white/50 font-mono text-xs uppercase tracking-widest">Loading…</div>}

      {!loading && data && data.entries && tab === "cash-book" && <CashBankBook data={data} account="cash" reload={load} />}
      {!loading && data && data.entries && tab === "bank-book" && <CashBankBook data={data} showMethod account="bank" reload={load} />}
      {!loading && data && data.rows && data.totals && tab === "sales-register" && <SalesRegister data={data} />}
      {!loading && data && data.rows && data.totals && tab === "purchase-register" && <PurchaseRegister data={data} reload={load} />}
      {!loading && data && data.parties && tab === "debtors" && <PartyList data={data} kind="debtor" reload={load} />}
      {!loading && data && data.parties && tab === "creditors" && <PartyList data={data} kind="creditor" reload={load} />}
      {!loading && data && data.entries && data.balanced !== undefined && tab === "journal" && <JournalView data={data} />}
      {!loading && data && data.accounts && tab === "trial-balance" && <TrialBalanceView data={data} />}
      {!loading && data && data.assets && tab === "balance-sheet" && <BalanceSheet data={data} />}
      {!loading && data && data.revenue && tab === "pnl" && <PnLView data={data} />}
      {!loading && data && data.b2cs_summary && tab === "gstr1" && <Gstr1View data={data} month={(to || today()).slice(0,7)} />}
      {!loading && data && data.counts && tab === "tally-export" && <TallyExportView data={data} until={to || today()} />}

      {showPurchase && <PurchaseModal onClose={() => setShowPurchase(false)} onDone={() => { setShowPurchase(false); load(); }} />}
    </div>
  );
}

function QuickRange({ onPick }) {
  const t = today();
  const set = (days) => {
    const d = new Date(); d.setDate(d.getDate() - days);
    onPick(d.toISOString().slice(0, 10), t);
  };
  const setMonth = () => { const d = new Date(); d.setDate(1); onPick(d.toISOString().slice(0, 10), t); };
  return (
    <div className="flex gap-1" data-testid="quick-range">
      {[["Today", () => onPick(t, t)], ["7d", () => set(7)], ["30d", () => set(30)], ["Month", setMonth]].map(([l, fn]) => (
        <button key={l} onClick={fn} className="text-[10px] font-mono uppercase tracking-widest border border-white/15 px-2.5 py-1.5 hover:border-brand-blue">{l}</button>
      ))}
    </div>
  );
}

function CashBankBook({ data, showMethod, account, reload }) {
  const [showOb, setShowOb] = useState(false);
  return (
    <div data-testid="cashbank-book">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-3">
        <div className="text-[11px] font-mono uppercase tracking-widest text-white/50">
          {data.has_opening_balance
            ? <>Opening balance: <span className="brand-blue">{formatINR(data.opening_balance)}</span> · Tally-style running balance from voucher entries.</>
            : <>No opening balance set — first click <span className="brand-orange">Set Opening Balance</span> to start proper book-keeping.</>}
        </div>
        <button onClick={() => setShowOb(true)} data-testid={`set-ob-${account}-btn`}
          className="inline-flex items-center gap-1 border border-brand-orange text-brand-orange px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest hover:bg-brand-orange hover:text-black transition">
          <Plus size={12} /> Set Opening Balance
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Stat label="Total In"  value={data.total_in}  color="green" />
        <Stat label="Total Out" value={data.total_out} color="red" />
        <Stat label="Net"       value={data.net}       color="blue" />
        <Stat label="Closing balance" value={data.closing_balance} color="orange" testid="closing-balance" />
      </div>
      <table className="w-full text-sm border border-white/12">
        <thead className="border-b border-white/10 text-[10px] font-mono uppercase tracking-widest text-white/50">
          <tr>
            <th className="text-left px-3 py-2">Date</th>
            <th className="text-left px-3 py-2">Time</th>
            {showMethod && <th className="text-left px-3 py-2">Method</th>}
            <th className="text-left px-3 py-2">Source</th>
            <th className="text-left px-3 py-2">Party</th>
            <th className="text-left px-3 py-2">Ref</th>
            <th className="text-right px-3 py-2">In</th>
            <th className="text-right px-3 py-2">Out</th>
            <th className="text-right px-3 py-2">Balance</th>
          </tr>
        </thead>
        <tbody>
          {data.entries.length === 0 && <tr><td colSpan="9" className="text-center py-8 text-white/40">No transactions.</td></tr>}
          {data.entries.map((e, i) => (
            <tr key={i} className={`border-b border-white/6 hover:bg-white/5 ${e.direction === "opening" ? "bg-brand-orange/10" : ""}`}>
              <td className="px-3 py-2 font-mono text-xs">{(e.at || "").slice(0, 10)}</td>
              <td className="px-3 py-2 font-mono text-[10px] text-white/50">{(e.at || "").slice(11, 16)}</td>
              {showMethod && <td className="px-3 py-2 uppercase text-[10px] font-mono tracking-widest brand-orange">{e.method}</td>}
              <td className={`px-3 py-2 text-[11px] ${e.direction === "opening" ? "brand-orange font-bold uppercase tracking-widest" : ""}`}>{e.source}</td>
              <td className="px-3 py-2">{e.party}</td>
              <td className="px-3 py-2 font-mono text-[10px] text-white/50">{e.ref_no || "—"}</td>
              <td className="px-3 py-2 text-right font-mono text-green-400">{e.direction === "in" ? formatINR(e.amount) : (e.direction === "opening" ? formatINR(e.amount) : "—")}</td>
              <td className="px-3 py-2 text-right font-mono text-red-400">{e.direction === "out" ? formatINR(e.amount) : "—"}</td>
              <td className="px-3 py-2 text-right font-mono brand-blue">{formatINR(e.running_balance)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {showOb && (
        <OpeningBalanceModal
          account={account}
          onClose={() => setShowOb(false)}
          onDone={() => { setShowOb(false); if (reload) reload(); }}
        />
      )}
    </div>
  );
}

function OpeningBalanceModal({ account, onClose, onDone }) {
  const [form, setForm] = useState({
    amount: 0,
    effective_from: today(),
    label: account === "cash" ? "Cash Drawer" : "Bank Account",
    note: "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const amt = Number(form.amount);
    if (amt < 0) { toast.error("Amount must be ≥ 0"); return; }
    setBusy(true);
    try {
      await api.post("/admin/ledgers/opening-balance", {
        account, amount: amt,
        effective_from: form.effective_from,
        label: form.label, note: form.note || undefined,
      });
      toast.success(`Opening balance set for ${account}`);
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} data-testid="opening-balance-modal"
        className="w-full max-w-md bg-[#131A2B] border border-brand-orange/40">
        <div className="p-4 border-b border-white/10 flex justify-between items-center">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">// Tally-style opening balance</div>
            <div className="font-display text-lg font-black uppercase mt-0.5">Set {account} Opening Balance</div>
          </div>
          <button type="button" onClick={onClose} className="text-white/50"><X size={16} /></button>
        </div>
        <div className="p-4 grid grid-cols-2 gap-3">
          <Field label="Amount ₹" span={2}>
            <input type="number" min="0" step="0.01" required value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              data-testid="ob-amount"
              className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" />
          </Field>
          <Field label="Effective from">
            <input type="date" required value={form.effective_from}
              onChange={(e) => setForm({ ...form, effective_from: e.target.value })}
              data-testid="ob-date"
              className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" />
          </Field>
          <Field label="Account label">
            <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
              data-testid="ob-label"
              className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" />
          </Field>
          <Field label="Note (opt)" span={2}>
            <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
              placeholder="FY 2026 opening balance as per audit"
              className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" />
          </Field>
        </div>
        <div className="p-4 border-t border-white/10 flex gap-2">
          <button type="button" onClick={onClose} className="flex-1 border border-white/15 py-2.5 font-mono text-xs uppercase tracking-widest">Cancel</button>
          <button type="submit" disabled={busy} data-testid="ob-save"
            className="flex-[2] bg-brand-orange text-black py-2.5 font-bold text-xs uppercase tracking-widest disabled:opacity-50">
            {busy ? "Saving…" : `Save Opening Balance`}
          </button>
        </div>
      </form>
    </div>
  );
}

function SalesRegister({ data }) {
  const [billDetail, setBillDetail] = useState(null);
  return (
    <div data-testid="sales-register">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <Stat label="Count" value={data.totals.count} raw />
        <Stat label="Subtotal" value={data.totals.subtotal} color="blue" />
        <Stat label="GST" value={data.totals.tax} color="orange" />
        <Stat label="Paid" value={data.totals.paid_total} color="green" />
        <Stat label="Pending" value={data.totals.pending_total} color="red" />
      </div>
      <table className="w-full text-sm border border-white/12">
        <thead className="border-b border-white/10 text-[10px] font-mono uppercase tracking-widest text-white/50">
          <tr>
            <th className="text-left px-3 py-2">Date</th>
            <th className="text-left px-3 py-2">Source</th>
            <th className="text-left px-3 py-2">Reference</th>
            <th className="text-left px-3 py-2">Party</th>
            <th className="text-right px-3 py-2">Subtotal</th>
            <th className="text-right px-3 py-2">GST</th>
            <th className="text-right px-3 py-2">Total</th>
            <th className="text-left px-3 py-2">Status</th>
            <th className="text-right px-3 py-2">Bill</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.length === 0 && <tr><td colSpan="9" className="text-center py-8 text-white/40">No sales.</td></tr>}
          {data.rows.map((r, i) => (
            <tr key={i} onClick={() => setBillDetail({ kind: r.source === "booking" ? "booking" : "invoice", id: r.id || r.ref_id, label: r.party })}
              className="border-b border-white/6 hover:bg-white/5 cursor-pointer"
              data-testid={`sales-row-${i}`}>
              <td className="px-3 py-2 font-mono text-xs">{r.date}</td>
              <td className="px-3 py-2 text-[11px] uppercase tracking-widest text-white/70">{r.source}</td>
              <td className="px-3 py-2 font-mono text-[10px] text-white/50">{r.reference}</td>
              <td className="px-3 py-2">{r.party}</td>
              <td className="px-3 py-2 text-right font-mono">{formatINR(r.amount)}</td>
              <td className="px-3 py-2 text-right font-mono brand-orange">{formatINR(r.tax_amount)}</td>
              <td className="px-3 py-2 text-right font-mono font-bold brand-blue">{formatINR(r.total)}</td>
              <td className="px-3 py-2 text-[10px] font-mono uppercase tracking-widest">
                <span className={r.payment_status === "paid" ? "text-green-400" : r.payment_status === "partial" ? "text-yellow-400" : "text-red-400"}>{r.payment_status}</span>
              </td>
              <td className="px-3 py-2 text-right"><FileText size={12} className="inline text-white/50" /></td>
            </tr>
          ))}
        </tbody>
      </table>
      {billDetail && <BillDetailModal kind={billDetail.kind} id={billDetail.id} label={billDetail.label} onClose={() => setBillDetail(null)} />}
    </div>
  );
}

function PurchaseRegister({ data, reload }) {
  const [billDetail, setBillDetail] = useState(null);
  const del = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this purchase?")) return;
    try { await api.delete(`/admin/purchases/${id}`); toast.success("Deleted"); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div data-testid="purchase-register">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <Stat label="Count" value={data.totals.count} raw />
        <Stat label="Subtotal" value={data.totals.subtotal} color="blue" />
        <Stat label="GST" value={data.totals.tax} color="orange" />
        <Stat label="Paid" value={data.totals.paid_total} color="green" />
        <Stat label="Pending" value={data.totals.pending_total} color="red" />
      </div>
      <table className="w-full text-sm border border-white/12">
        <thead className="border-b border-white/10 text-[10px] font-mono uppercase tracking-widest text-white/50">
          <tr>
            <th className="text-left px-3 py-2">Date</th>
            <th className="text-left px-3 py-2">Supplier</th>
            <th className="text-left px-3 py-2">Category</th>
            <th className="text-left px-3 py-2">Bill No</th>
            <th className="text-right px-3 py-2">Subtotal</th>
            <th className="text-right px-3 py-2">GST</th>
            <th className="text-right px-3 py-2">Total</th>
            <th className="text-left px-3 py-2">Status</th>
            <th className="text-right px-3 py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.length === 0 && <tr><td colSpan="9" className="text-center py-8 text-white/40">No purchases in range.</td></tr>}
          {data.rows.map((r) => (
            <tr key={r.id} onClick={() => setBillDetail({ kind: "purchase", id: r.id, label: r.supplier })}
              className="border-b border-white/6 hover:bg-white/5 cursor-pointer" data-testid={`purchase-row-${r.id}`}>
              <td className="px-3 py-2 font-mono text-xs">{r.date}</td>
              <td className="px-3 py-2">{r.supplier}</td>
              <td className="px-3 py-2 text-[11px] uppercase tracking-widest text-white/70">{r.category}</td>
              <td className="px-3 py-2 font-mono text-[10px] text-white/50">{r.bill_no || "—"}</td>
              <td className="px-3 py-2 text-right font-mono">{formatINR(r.amount)}</td>
              <td className="px-3 py-2 text-right font-mono brand-orange">{formatINR(r.tax_amount)}</td>
              <td className="px-3 py-2 text-right font-mono font-bold brand-blue">{formatINR(r.total)}</td>
              <td className="px-3 py-2 text-[10px] font-mono uppercase tracking-widest">
                <span className={r.payment_status === "paid" ? "text-green-400" : "text-yellow-400"}>{r.payment_status}</span>
              </td>
              <td className="px-3 py-2 text-right">
                <button onClick={(e) => del(r.id, e)} className="text-red-400 hover:text-red-300"><Trash2 size={12} /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {billDetail && <BillDetailModal kind="purchase" id={billDetail.id} label={billDetail.label} onClose={() => setBillDetail(null)} />}
    </div>
  );
}

function PartyList({ data, kind, reload }) {
  const items = data.parties;
  const [payTarget, setPayTarget] = useState(null); // {purchase_id, supplier, outstanding}
  const [collectTarget, setCollectTarget] = useState(null); // {source_type, source_id, party, outstanding, invoice_number}
  const [stmtKey, setStmtKey] = useState(null);       // party.parent_id / phone / supplier name
  const [billDetail, setBillDetail] = useState(null); // {kind, id}
  const aging = data.aging_totals || null;
  return (
    <div data-testid={`${kind}-list`}>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <Stat label={`${kind === "debtor" ? "Debtors" : "Creditors"} count`} value={data.count} raw />
        <Stat label="Total outstanding" value={data.total_outstanding} color={kind === "debtor" ? "green" : "red"} testid={`${kind}-total`} />
      </div>
      {aging && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4" data-testid={`${kind}-aging`}>
          {[
            ["0-30",  "0 – 30 days",  "text-green-400"],
            ["31-60", "31 – 60 days", "text-yellow-400"],
            ["61-90", "61 – 90 days", "text-orange-400"],
            ["90+",   "90+ days",     "text-red-400"],
          ].map(([k, label, cls]) => (
            <div key={k} className="border border-white/12 bg-[#0f1526] p-3" data-testid={`aging-${kind}-${k}`}>
              <div className="font-mono text-[10px] uppercase tracking-widest text-white/50">{label}</div>
              <div className={`font-display text-lg font-black mt-1 ${cls}`}>{formatINR(aging[k] || 0)}</div>
            </div>
          ))}
        </div>
      )}
      <div className="space-y-3">
        {items.length === 0 && <div className="text-center py-8 text-white/40">No {kind === "debtor" ? "receivables" : "payables"}.</div>}
        {items.map((p, i) => (
          <div key={i} className="border border-white/12 bg-[#131A2B] p-4" data-testid={`${kind}-row-${i}`}>
            <div className="flex justify-between items-start gap-3 flex-wrap">
              <div className="flex-1">
                <button onClick={() => setStmtKey(kind === "debtor" ? (p.parent_id || p.phone || p.party) : p.supplier)}
                  data-testid={`${kind}-open-statement-${i}`}
                  className="font-display text-lg font-black uppercase text-left hover:brand-orange transition">
                  {p.party || p.supplier} <span className="text-[10px] font-mono text-brand-orange ml-1">◈ View Ledger</span>
                </button>
                <div className="text-[10px] font-mono text-white/50 mt-1">
                  {kind === "debtor" ? "invoices" : "bills"}: {p.count} · oldest: {p.oldest_days != null ? `${p.oldest_days}d ago` : "—"} · phone: {p.phone || "—"}
                </div>
              </div>
              <div className={`font-display text-2xl font-black ${kind === "debtor" ? "brand-blue" : "text-red-400"}`}>
                {formatINR(p.outstanding)}
              </div>
            </div>
            {p.aging && (
              <div className="mt-3 grid grid-cols-4 gap-1 text-[10px] font-mono">
                {["0-30","31-60","61-90","90+"].map((k) => (
                  <div key={k} className="border border-white/10 px-2 py-1">
                    <div className="text-white/40 uppercase tracking-widest">{k}d</div>
                    <div className={p.aging[k] > 0 ? (k === "90+" ? "text-red-400" : k === "61-90" ? "text-orange-400" : k === "31-60" ? "text-yellow-400" : "text-green-400") : "text-white/30"}>
                      {formatINR(p.aging[k] || 0)}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-1 text-[10px] font-mono text-white/60">
              {(p.invoices || p.purchases || []).slice(0, 5).map((x, j) => (
                <div key={j} className="flex justify-between border-b border-white/6 py-1 items-center">
                  <button className="truncate text-left hover:text-brand-orange"
                    onClick={() => {
                      const isBooking = (x.service_type || "").startsWith("booking");
                      const detailKind = kind === "creditor" ? "purchase" : (isBooking ? "booking" : "invoice");
                      setBillDetail({ kind: detailKind, id: x.id, label: p.party || p.supplier });
                    }}
                    data-testid={`${kind}-open-bill-${x.id}`}>
                    {x.date || (x.at || "").slice(0, 10)} · {x.service_type || x.category} · {x.invoice_number || x.bill_no || ""}
                    {x.aging_bucket && (
                      <span className={`ml-1 px-1 py-0.5 text-[9px] ${x.aging_bucket === "90+" ? "bg-red-500/20 text-red-300" : x.aging_bucket === "61-90" ? "bg-orange-500/20 text-orange-300" : x.aging_bucket === "31-60" ? "bg-yellow-500/20 text-yellow-300" : "bg-green-500/15 text-green-300"}`}>
                        {x.aging_bucket}d
                      </span>
                    )}
                  </button>
                  <span className="flex items-center gap-2 whitespace-nowrap">
                    <span className="brand-orange">{formatINR(x.outstanding)}</span>
                    {kind === "creditor" && (
                      <button
                        onClick={() => setPayTarget({ purchase_id: x.id, supplier: p.supplier, outstanding: x.outstanding, bill_no: x.bill_no })}
                        data-testid={`pay-creditor-${x.id}`}
                        className="border border-brand-orange text-brand-orange px-2 py-0.5 text-[9px] uppercase tracking-widest hover:bg-brand-orange hover:text-black transition">
                        Pay Now
                      </button>
                    )}
                    {kind === "debtor" && (
                      <button
                        onClick={() => {
                          const isBooking = (x.service_type || "").startsWith("booking");
                          setCollectTarget({
                            source_type: isBooking ? "booking" : "invoice",
                            source_id: x.id,
                            party: p.party,
                            outstanding: x.outstanding,
                            reference: x.invoice_number || "",
                          });
                        }}
                        data-testid={`collect-debtor-${x.id}`}
                        className="border border-green-400 text-green-400 px-2 py-0.5 text-[9px] uppercase tracking-widest hover:bg-green-400 hover:text-black transition">
                        Collect ₹
                      </button>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {payTarget && (
        <PayCreditorModal
          target={payTarget}
          onClose={() => setPayTarget(null)}
          onDone={() => { setPayTarget(null); if (reload) reload(); }}
        />
      )}
      {collectTarget && (
        <CollectDebtorModal
          target={collectTarget}
          onClose={() => setCollectTarget(null)}
          onDone={() => { setCollectTarget(null); if (reload) reload(); }}
        />
      )}
      {stmtKey && (
        <PartyStatementModal kind={kind} partyKey={stmtKey} onClose={() => setStmtKey(null)} />
      )}
      {billDetail && (
        <BillDetailModal kind={billDetail.kind} id={billDetail.id} label={billDetail.label} onClose={() => setBillDetail(null)} />
      )}
    </div>
  );
}

function CollectDebtorModal({ target, onClose, onDone }) {
  const [amount, setAmount] = useState(target.outstanding);
  const [method, setMethod] = useState("cash");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const amt = Number(amount);
    if (amt <= 0 || amt > target.outstanding + 0.01) {
      toast.error(`Amount must be > 0 and ≤ ${formatINR(target.outstanding)}`); return;
    }
    setBusy(true);
    try {
      await api.post("/admin/ledgers/receipts/collect", {
        source_type: target.source_type,
        source_id: target.source_id,
        amount: amt, method, note: note || undefined,
      });
      toast.success(`Received ${formatINR(amt)} from ${target.party} · Cash/Bank book updated`);
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} data-testid="collect-debtor-modal"
        className="w-full max-w-md bg-[#131A2B] border border-green-400/40">
        <div className="p-4 border-b border-white/10 flex justify-between items-center">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-green-400">// Tally receipt voucher (F6)</div>
            <div className="font-display text-lg font-black uppercase mt-0.5">Collect from {target.party}</div>
            <div className="text-[10px] font-mono text-white/50 mt-1">Ref {target.reference || "—"} · outstanding {formatINR(target.outstanding)}</div>
          </div>
          <button type="button" onClick={onClose} className="text-white/50"><X size={16} /></button>
        </div>
        <div className="p-4 grid grid-cols-2 gap-3">
          <Field label="Amount ₹" span={2}>
            <input type="number" min="0" max={target.outstanding} step="0.01" value={amount}
              onChange={(e) => setAmount(e.target.value)} data-testid="collect-amount" required
              className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" />
          </Field>
          <Field label="Method">
            <select value={method} onChange={(e) => setMethod(e.target.value)} data-testid="collect-method"
              className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm">
              {["cash","upi","card","cheque","bank_transfer"].map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </Field>
          <Field label="Note (opt)">
            <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="collect-note"
              className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" />
          </Field>
        </div>
        <div className="p-4 border-t border-white/10 flex gap-2">
          <button type="button" onClick={onClose} className="flex-1 border border-white/15 py-2.5 font-mono text-xs uppercase tracking-widest">Cancel</button>
          <button type="submit" disabled={busy} data-testid="collect-confirm"
            className="flex-[2] bg-green-400 text-black py-2.5 font-bold text-xs uppercase tracking-widest disabled:opacity-50">
            {busy ? "Receiving…" : `Confirm Receipt · ${formatINR(Number(amount) || 0)}`}
          </button>
        </div>
      </form>
    </div>
  );
}

function PayCreditorModal({ target, onClose, onDone }) {
  const [amount, setAmount] = useState(target.outstanding);
  const [method, setMethod] = useState("cash");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const amt = Number(amount);
    if (amt <= 0 || amt > target.outstanding + 0.01) {
      toast.error(`Amount must be > 0 and ≤ ${formatINR(target.outstanding)}`); return;
    }
    setBusy(true);
    try {
      await api.post("/admin/purchases/pay", {
        purchase_id: target.purchase_id, amount: amt, method, note: note || undefined,
      });
      toast.success(`Paid ${formatINR(amt)} to ${target.supplier}`);
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} data-testid="pay-creditor-modal"
        className="w-full max-w-md bg-[#131A2B] border border-brand-orange/40">
        <div className="p-4 border-b border-white/10 flex justify-between items-center">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">// Vendor payoff</div>
            <div className="font-display text-lg font-black uppercase mt-0.5">Pay {target.supplier}</div>
            <div className="text-[10px] font-mono text-white/50 mt-1">Bill {target.bill_no || "—"} · outstanding {formatINR(target.outstanding)}</div>
          </div>
          <button type="button" onClick={onClose} className="text-white/50"><X size={16} /></button>
        </div>
        <div className="p-4 grid grid-cols-2 gap-3">
          <Field label="Amount ₹" span={2}>
            <input type="number" min="0" max={target.outstanding} step="0.01" value={amount}
              onChange={(e) => setAmount(e.target.value)} data-testid="pay-amount" required
              className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" />
          </Field>
          <Field label="Method">
            <select value={method} onChange={(e) => setMethod(e.target.value)} data-testid="pay-method"
              className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm">
              {["cash","upi","card","cheque","bank_transfer"].map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </Field>
          <Field label="Note (opt)">
            <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="pay-note"
              className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" />
          </Field>
        </div>
        <div className="p-4 border-t border-white/10 flex gap-2">
          <button type="button" onClick={onClose} className="flex-1 border border-white/15 py-2.5 font-mono text-xs uppercase tracking-widest">Cancel</button>
          <button type="submit" disabled={busy} data-testid="pay-confirm"
            className="flex-[2] bg-brand-orange text-black py-2.5 font-bold text-xs uppercase tracking-widest disabled:opacity-50">
            {busy ? "Paying…" : `Confirm · ${formatINR(Number(amount) || 0)}`}
          </button>
        </div>
      </form>
    </div>
  );
}

function JournalView({ data }) {
  return (
    <div data-testid="journal-view">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Stat label="Entries" value={data.count} raw />
        <Stat label="Total Debit" value={data.total_debit} color="green" testid="j-debit" />
        <Stat label="Total Credit" value={data.total_credit} color="red" testid="j-credit" />
        <div className="p-4 border border-white/12 bg-[#131A2B]" data-testid="j-balanced">
          <div className="font-mono text-[10px] uppercase tracking-widest text-white/50">Balanced?</div>
          <div className={`font-display text-xl font-black mt-1 ${data.balanced ? "text-green-400" : "text-red-400"}`}>
            {data.balanced ? "YES · Dr = Cr" : "NO · Mismatch"}
          </div>
        </div>
      </div>
      <div className="space-y-3">
        {data.entries.length === 0 && <div className="text-center py-8 text-white/40">No journal entries in range.</div>}
        {data.entries.map((e, i) => (
          <div key={i} className="border border-white/12 bg-[#131A2B]" data-testid={`journal-entry-${i}`}>
            <div className="flex justify-between items-center border-b border-white/10 px-4 py-2">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">// {(e.at || "").slice(0,10)} · {(e.at || "").slice(11,16)}</div>
                <div className="text-sm mt-0.5">{e.narration}</div>
              </div>
              <div className="font-mono text-[10px] text-white/40">{e.reference}</div>
            </div>
            <table className="w-full text-xs">
              <thead className="text-[10px] font-mono uppercase tracking-widest text-white/40">
                <tr>
                  <th className="text-left px-4 py-1.5">Account</th>
                  <th className="text-right px-4 py-1.5">Debit</th>
                  <th className="text-right px-4 py-1.5">Credit</th>
                </tr>
              </thead>
              <tbody>
                {e.lines.map((l, j) => (
                  <tr key={j} className="border-t border-white/6">
                    <td className={`px-4 py-1.5 ${l.debit > 0 ? "font-bold" : "pl-8 text-white/70"}`}>{l.account}</td>
                    <td className="px-4 py-1.5 text-right font-mono text-green-400">{l.debit > 0 ? formatINR(l.debit) : "—"}</td>
                    <td className="px-4 py-1.5 text-right font-mono text-red-400">{l.credit > 0 ? formatINR(l.credit) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}

function TrialBalanceView({ data }) {
  return (
    <div data-testid="trial-balance-view">
      <div className="grid grid-cols-3 gap-3 mb-4">
        <Stat label="Total Debit" value={data.total_debit} color="green" testid="tb-debit" />
        <Stat label="Total Credit" value={data.total_credit} color="red" testid="tb-credit" />
        <div className="p-4 border border-white/12 bg-[#131A2B]" data-testid="tb-balanced">
          <div className="font-mono text-[10px] uppercase tracking-widest text-white/50">Balanced?</div>
          <div className={`font-display text-xl font-black mt-1 ${data.balanced ? "text-green-400" : "text-red-400"}`}>
            {data.balanced ? "YES · Dr = Cr" : "NO · Mismatch"}
          </div>
        </div>
      </div>
      <table className="w-full text-sm border border-white/12">
        <thead className="border-b border-white/10 text-[10px] font-mono uppercase tracking-widest text-white/50">
          <tr>
            <th className="text-left px-3 py-2">Account</th>
            <th className="text-right px-3 py-2">Debit</th>
            <th className="text-right px-3 py-2">Credit</th>
            <th className="text-right px-3 py-2">Net Dr</th>
            <th className="text-right px-3 py-2">Net Cr</th>
          </tr>
        </thead>
        <tbody>
          {data.accounts.length === 0 && <tr><td colSpan="5" className="text-center py-8 text-white/40">No postings in range.</td></tr>}
          {data.accounts.map((a, i) => (
            <tr key={i} className="border-b border-white/6 hover:bg-white/5" data-testid={`tb-row-${i}`}>
              <td className="px-3 py-2 font-medium">{a.account}</td>
              <td className="px-3 py-2 text-right font-mono">{a.debit > 0 ? formatINR(a.debit) : "—"}</td>
              <td className="px-3 py-2 text-right font-mono">{a.credit > 0 ? formatINR(a.credit) : "—"}</td>
              <td className="px-3 py-2 text-right font-mono brand-blue">{a.net_debit > 0 ? formatINR(a.net_debit) : "—"}</td>
              <td className="px-3 py-2 text-right font-mono brand-orange">{a.net_credit > 0 ? formatINR(a.net_credit) : "—"}</td>
            </tr>
          ))}
        </tbody>
        <tfoot className="border-t-2 border-white/20 font-bold">
          <tr>
            <td className="px-3 py-2 uppercase tracking-widest font-mono text-[10px]">TOTAL</td>
            <td className="px-3 py-2 text-right font-mono brand-blue">{formatINR(data.total_debit)}</td>
            <td className="px-3 py-2 text-right font-mono brand-orange">{formatINR(data.total_credit)}</td>
            <td colSpan="2"></td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function Gstr1View({ data, month }) {
  const downloadCsv = async () => {
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/ledgers/gstr1?month=${month}&format=csv`;
      const r = await fetch(url, { credentials: "include" });
      if (!r.ok) throw new Error("Download failed");
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `GSTR1-${month}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { toast.error("CSV download failed"); }
  };
  return (
    <div data-testid="gstr1-view">
      <div className="border border-brand-orange/40 bg-brand-orange/5 p-4 mb-4 flex flex-wrap items-center gap-3 justify-between">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">// GSTR-1 · Month {month}</div>
          <div className="text-sm mt-0.5">
            {data.supplier_legal_name || "—"}  ·  GSTIN {data.supplier_gstin || "—"}  ·  State {data.supplier_state || "—"}
          </div>
        </div>
        <button onClick={downloadCsv} data-testid="gstr1-csv-btn"
          className="inline-flex items-center gap-2 bg-brand-orange text-black px-4 py-2 text-xs uppercase font-bold tracking-widest">
          <Download size={12} /> Download CSV
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <Stat label="Count" value={data.totals.count} raw />
        <Stat label="Taxable Value" value={data.totals.taxable_value} color="blue" testid="gstr1-taxable" />
        <Stat label="CGST" value={data.totals.cgst} color="orange" />
        <Stat label="SGST" value={data.totals.sgst} color="orange" />
        <Stat label="Grand Total" value={data.totals.total} color="green" testid="gstr1-total" />
      </div>

      {/* B2CS Summary */}
      <div className="mb-4">
        <div className="font-display text-sm font-black uppercase brand-blue mb-2">B2CS · Rate-wise Summary</div>
        <table className="w-full text-sm border border-white/12">
          <thead className="border-b border-white/10 text-[10px] font-mono uppercase tracking-widest text-white/50">
            <tr>
              <th className="text-left px-3 py-2">GST %</th>
              <th className="text-right px-3 py-2">Taxable Value</th>
              <th className="text-right px-3 py-2">CGST</th>
              <th className="text-right px-3 py-2">SGST</th>
              <th className="text-right px-3 py-2">Total</th>
              <th className="text-right px-3 py-2">Count</th>
            </tr>
          </thead>
          <tbody>
            {data.b2cs_summary.length === 0 && <tr><td colSpan="6" className="text-center py-8 text-white/40">No sales in month.</td></tr>}
            {data.b2cs_summary.map((r, i) => (
              <tr key={i} className="border-b border-white/6" data-testid={`b2cs-row-${i}`}>
                <td className="px-3 py-2 font-mono">{r.gst_percent}%</td>
                <td className="px-3 py-2 text-right font-mono">{formatINR(r.taxable_value)}</td>
                <td className="px-3 py-2 text-right font-mono brand-orange">{formatINR(r.cgst)}</td>
                <td className="px-3 py-2 text-right font-mono brand-orange">{formatINR(r.sgst)}</td>
                <td className="px-3 py-2 text-right font-mono brand-blue">{formatINR(r.total)}</td>
                <td className="px-3 py-2 text-right font-mono text-white/60">{r.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* HSN Summary */}
      <div className="mb-4">
        <div className="font-display text-sm font-black uppercase brand-orange mb-2">HSN Summary</div>
        <table className="w-full text-sm border border-white/12">
          <thead className="border-b border-white/10 text-[10px] font-mono uppercase tracking-widest text-white/50">
            <tr>
              <th className="text-left px-3 py-2">HSN</th>
              <th className="text-left px-3 py-2">Rate</th>
              <th className="text-right px-3 py-2">Taxable Value</th>
              <th className="text-right px-3 py-2">CGST</th>
              <th className="text-right px-3 py-2">SGST</th>
              <th className="text-right px-3 py-2">Total</th>
              <th className="text-right px-3 py-2">Qty</th>
            </tr>
          </thead>
          <tbody>
            {data.hsn_summary.map((r, i) => (
              <tr key={i} className="border-b border-white/6">
                <td className="px-3 py-2 font-mono">{r.hsn}</td>
                <td className="px-3 py-2 font-mono">{r.gst_percent}%</td>
                <td className="px-3 py-2 text-right font-mono">{formatINR(r.taxable_value)}</td>
                <td className="px-3 py-2 text-right font-mono">{formatINR(r.cgst)}</td>
                <td className="px-3 py-2 text-right font-mono">{formatINR(r.sgst)}</td>
                <td className="px-3 py-2 text-right font-mono">{formatINR(r.total)}</td>
                <td className="px-3 py-2 text-right font-mono">{r.qty}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TallyExportView({ data, until }) {
  const c = data.counts;
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/ledgers/tally-export?until=${until}`;
      const r = await fetch(url, { credentials: "include" });
      if (!r.ok) throw new Error("Export failed");
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `TallyExport_${(data.company || 'export').replace(/\s+/g, '_')}_upto_${until}.xml`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success(`Tally XML downloaded · ${(data.size_bytes / 1024).toFixed(1)} KB`);
    } catch (e) { toast.error("Export failed"); } finally { setBusy(false); }
  };

  return (
    <div data-testid="tally-export-view" className="space-y-6">
      <div className="border border-brand-orange bg-brand-orange/10 p-5 flex flex-wrap justify-between items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">// Tally Prime import file</div>
          <div className="font-display text-2xl md:text-3xl font-black uppercase mt-1">Export to Tally Prime</div>
          <div className="text-xs text-white/60 mt-2">
            Downloads a complete <span className="brand-blue">.xml</span> file (native Tally format) covering everything from inception up to <span className="font-mono brand-orange">{until}</span>.
            Import via <span className="brand-blue">Gateway of Tally → Import → Vouchers → point to this file</span>. Every masters entry uses <span className="font-mono">ACTION=&quot;Create&quot;</span> with <span className="font-mono">{"@@DUPCOMBINE"}</span> so re-imports are safe.
          </div>
        </div>
        <button onClick={download} disabled={busy} data-testid="tally-download-btn"
          className="inline-flex items-center gap-2 bg-brand-orange text-black px-5 py-3 font-bold text-sm uppercase tracking-widest disabled:opacity-50 hover:bg-white transition">
          <Download size={16} /> {busy ? "Preparing…" : "Download XML"}
        </button>
      </div>

      <div>
        <div className="font-display text-sm font-black uppercase text-white/80 mb-2">Masters that will be imported</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Ledger accounts" value={c.ledgers} raw testid="tally-ledgers" />
          <Stat label="Stock items" value={c.stock_items} raw testid="tally-stock" />
          <div className="col-span-2 md:col-span-2 border border-white/12 p-3 bg-[#131A2B]">
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/50">Auto-created Tally groups used</div>
            <div className="text-[11px] text-white/70 mt-1">
              Sundry Debtors · Sundry Creditors · Cash-in-Hand · Bank Accounts · Sales Accounts · Purchase Accounts · Duties &amp; Taxes · Current Liabilities · Stock-in-Hand
            </div>
          </div>
        </div>
      </div>

      <div>
        <div className="font-display text-sm font-black uppercase text-white/80 mb-2">Vouchers that will be imported</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Stat label="Sales" value={c.sales_vouchers} raw testid="tally-sales-vch" />
          <Stat label="Receipts" value={c.receipt_vouchers} raw color="green" testid="tally-receipt-vch" />
          <Stat label="Purchases" value={c.purchase_vouchers} raw color="orange" testid="tally-purchase-vch" />
          <Stat label="Payments" value={c.payment_vouchers} raw color="red" testid="tally-payment-vch" />
          <Stat label="Journal" value={c.journal_vouchers} raw color="blue" testid="tally-journal-vch" />
        </div>
        <div className="mt-3 flex items-center gap-4 text-[11px] font-mono text-white/50">
          <span>Total vouchers: <span className="brand-blue">{c.total_vouchers}</span></span>
          <span>Total masters: <span className="brand-blue">{c.total_masters}</span></span>
          <span>File size ≈ <span className="brand-orange">{(data.size_bytes / 1024).toFixed(1)} KB</span></span>
          <span>Generated at: <span className="text-white/70">{(data.generated_at || "").slice(11, 19)}</span></span>
        </div>
      </div>

      <div className="border border-white/12 bg-[#131A2B] p-4 text-xs text-white/70 space-y-2">
        <div className="font-display text-sm font-black uppercase brand-blue">How to import in Tally Prime</div>
        <ol className="list-decimal pl-4 space-y-1">
          <li>Open Tally Prime and select the company you want to import into (or create <span className="brand-orange">{data.company}</span>).</li>
          <li>From the Gateway, press <span className="font-mono brand-orange">Alt+O</span> (Import) → <span className="brand-orange">Vouchers</span> or <span className="brand-orange">Masters</span>.</li>
          <li>Give the file path where you saved the downloaded <span className="font-mono">.xml</span>.</li>
          <li>Duplicates policy is set to <span className="font-mono">Combine Opening Balances</span> automatically — safe to re-import any time.</li>
          <li>Once imported, verify Trial Balance in Tally matches this ERP&apos;s Trial Balance tab.</li>
        </ol>
      </div>
    </div>
  );
}

function BalanceSheet({ data }) {
  const { assets, liabilities, equity } = data;
  return (
    <div data-testid="balance-sheet">
      <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-3">As of {data.as_of}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border border-brand-blue/40 bg-brand-blue/5 p-4">
          <div className="font-display text-lg font-black uppercase brand-blue mb-3">Assets</div>
          <Line label="Cash on hand"           value={assets.cash_on_hand} testid="bs-cash" />
          <Line label="Bank balance"           value={assets.bank_balance} testid="bs-bank" />
          <Line label="Debtors (A/R)"          value={assets.accounts_receivable} testid="bs-ar" />
          <Line label="Kit stock value"        value={assets.kit_stock_value} />
          <Line label="Total Assets"           value={assets.total} bold testid="bs-assets-total" />
        </div>
        <div className="border border-brand-orange/40 bg-brand-orange/5 p-4">
          <div className="font-display text-lg font-black uppercase brand-orange mb-3">Liabilities</div>
          <Line label="Creditors (A/P)"         value={liabilities.accounts_payable} testid="bs-ap" />
          <Line label="Wallet credits (owed to parents)" value={liabilities.wallet_credits} />
          <Line label="Total Liabilities"       value={liabilities.total} bold testid="bs-liab-total" />
          <div className="mt-4 pt-3 border-t border-white/20">
            <Line label="Owner's Equity" value={equity} bold big testid="bs-equity" />
          </div>
        </div>
      </div>
    </div>
  );
}

function PnLView({ data }) {
  const { revenue, expenses, gross_profit_ex_gst, net_gst_liability } = data;
  return (
    <div data-testid="pnl">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <Stat label="Revenue (ex-GST)" value={revenue.subtotal} color="green" testid="pnl-revenue" />
        <Stat label="Expenses (ex-GST)" value={expenses.subtotal} color="red" testid="pnl-expenses" />
        <Stat label="Gross Profit" value={gross_profit_ex_gst} color={gross_profit_ex_gst >= 0 ? "blue" : "red"} testid="pnl-gross-profit" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border border-brand-blue/40 p-4">
          <div className="font-display font-black uppercase text-sm brand-blue mb-2">Revenue by source</div>
          {Object.entries(revenue.by_source).map(([k, v]) => (
            <Line key={k} label={`${k} (${v.count})`} value={v.subtotal} />
          ))}
        </div>
        <div className="border border-brand-orange/40 p-4">
          <div className="font-display font-black uppercase text-sm brand-orange mb-2">Expenses by category</div>
          {Object.entries(expenses.by_category).length === 0 && <div className="text-white/40 text-xs">No purchases in range.</div>}
          {Object.entries(expenses.by_category).map(([k, v]) => (
            <Line key={k} label={`${k} (${v.count})`} value={v.subtotal} />
          ))}
        </div>
      </div>
      <div className="mt-4 border border-white/12 p-4 text-[11px] font-mono">
        <div className="flex justify-between text-white/60"><span>GST collected on sales (output tax)</span><span className="brand-orange">{formatINR(revenue.gst)}</span></div>
        <div className="flex justify-between text-white/60"><span>GST paid on purchases (input tax credit)</span><span className="text-white/60">{formatINR(expenses.gst)}</span></div>
        <div className="flex justify-between mt-2 pt-2 border-t border-white/10 font-bold"><span>Net GST liability</span><span className={net_gst_liability >= 0 ? "brand-orange" : "text-green-400"}>{formatINR(net_gst_liability)}</span></div>
      </div>
    </div>
  );
}

function PurchaseModal({ onClose, onDone }) {
  const [form, setForm] = useState({
    date: today(), supplier: "", supplier_id: "", bill_no: "", category: "kit",
    description: "", gst_percent: 18, payment_method: "cash",
    payment_status: "paid", notes: "",
  });
  const [items, setItems] = useState([]);
  const [kits, setKits] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [showNewSupplier, setShowNewSupplier] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [rk, rs] = await Promise.all([
          api.get("/admin/kits"),
          api.get("/admin/suppliers"),
        ]);
        setKits(rk.data || []);
        setSuppliers(rs.data || []);
      } catch (e) { /* non-blocking */ }
    })();
  }, []);

  const pickSupplier = (sid) => {
    const s = suppliers.find((x) => x.id === sid);
    if (s) setForm({ ...form, supplier_id: sid, supplier: s.name });
    else setForm({ ...form, supplier_id: "", supplier: "" });
  };
  const onSupplierCreated = (created) => {
    setSuppliers([created, ...suppliers.filter((s) => s.id !== created.id)]);
    setForm({ ...form, supplier_id: created.id, supplier: created.name });
    setShowNewSupplier(false);
  };

  const addItem = () => setItems([...items, {
    mode: "existing", kit_id: "", name: "", hsn: "", sku: "",
    quantity: 1, unit_price: 0,
    gst_percent: form.gst_percent, category: form.category,
  }]);
  const rmItem = (idx) => setItems(items.filter((_, i) => i !== idx));
  const setItem = (idx, patch) => setItems(items.map((it, i) => i === idx ? { ...it, ...patch } : it));
  const pickKit = (idx, kid) => {
    const k = kits.find((x) => x.id === kid);
    setItem(idx, {
      kit_id: kid, name: k?.name || "", hsn: k?.hsn || "",
      unit_price: k?.unit_price || 0,
      category: k?.category || items[idx]?.category || form.category,
    });
  };

  // Per-line GST — Tally-style
  const subtotal = items.reduce((s, it) => s + Number(it.quantity || 0) * Number(it.unit_price || 0), 0);
  const gstAmt = items.reduce((s, it) => {
    const taxable = Number(it.quantity || 0) * Number(it.unit_price || 0);
    const rate = Number(it.gst_percent ?? form.gst_percent ?? 0);
    return s + taxable * rate / 100;
  }, 0);
  const grandTotal = subtotal + gstAmt;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.supplier.trim()) { toast.error("Supplier required"); return; }
    if (items.length === 0) { toast.error("Add at least one line item"); return; }
    for (const it of items) {
      if (!it.name?.trim() || Number(it.quantity) <= 0 || Number(it.unit_price) < 0) {
        toast.error("Every line needs name + quantity + price"); return;
      }
      if (it.mode === "existing" && !it.kit_id) {
        toast.error(`Pick a kit for "${it.name || 'row'}" or switch to "New item"`); return;
      }
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        supplier_id: form.supplier_id || undefined,
        gst_percent: Number(form.gst_percent || 0),
        amount: Number(subtotal.toFixed(2)),
        items: items.map((it) => ({
          kit_id: it.mode === "existing" ? it.kit_id : undefined,
          create_kit: it.mode === "new",
          name: it.name.trim(),
          hsn: it.hsn || undefined,
          sku: it.mode === "new" ? (it.sku || undefined) : undefined,
          category: it.category || undefined,
          gst_percent: it.gst_percent !== undefined && it.gst_percent !== "" ? Number(it.gst_percent) : undefined,
          quantity: Number(it.quantity),
          unit_price: Number(it.unit_price),
        })),
      };
      await api.post("/admin/purchases", payload);
      toast.success(`Purchase recorded · Stock updated for ${items.filter(i => i.mode !== "none").length} item(s)`);
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start md:items-center justify-center p-2 md:p-4 overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        data-testid="purchase-modal"
        className="w-full max-w-4xl bg-[#131A2B] border border-brand-orange/40 my-4">
        <div className="p-5 border-b border-white/10 flex justify-between items-center">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">// New purchase bill</div>
            <div className="font-display text-lg font-black uppercase mt-0.5">Log Purchase · Restock Inventory</div>
          </div>
          <button type="button" onClick={onClose} className="text-white/50"><X size={18} /></button>
        </div>

        {/* Bill header */}
        <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-3 border-b border-white/10">
          <Field label="Date"><input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="purchase-date" className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" /></Field>
          <Field label="Supplier" span={2}>
            <div className="flex gap-2">
              <select value={form.supplier_id} onChange={(e) => pickSupplier(e.target.value)}
                data-testid="purchase-supplier-picker"
                className="flex-1 bg-black/40 border border-white/15 px-3 py-2 text-sm">
                <option value="">— Select or type new below —</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}{s.gstin ? ` · ${s.gstin}` : ""}</option>
                ))}
              </select>
              <button type="button" onClick={() => setShowNewSupplier(true)}
                data-testid="add-supplier-btn"
                className="border border-brand-orange text-brand-orange px-2 py-1 text-[10px] font-mono uppercase tracking-widest whitespace-nowrap">
                + New
              </button>
            </div>
            <input required value={form.supplier}
              onChange={(e) => setForm({ ...form, supplier: e.target.value, supplier_id: "" })}
              data-testid="purchase-supplier" placeholder="Supplier name (auto-filled from picker)"
              className="w-full bg-black/30 border border-white/10 px-3 py-1.5 text-xs mt-1" />
          </Field>
          <Field label="Bill / Invoice No"><input value={form.bill_no} onChange={(e) => setForm({ ...form, bill_no: e.target.value })} data-testid="purchase-bill" placeholder="SW-2026/0142" className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" /></Field>
          <Field label="Category (default for lines)">
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="purchase-category" className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm">
              {["kit","consumable","repair","rent","utility","salary","other"].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Default GST %"><input type="number" min="0" step="0.01" value={form.gst_percent} onChange={(e) => setForm({ ...form, gst_percent: e.target.value })} data-testid="purchase-gst" className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" /></Field>
          <Field label="Description" span={2}><input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Restock cricket balls & bats" className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" /></Field>
        </div>

        {/* Line items */}
        <div className="p-5 border-b border-white/10">
          <div className="flex justify-between items-center mb-3">
            <div className="font-mono text-[11px] uppercase tracking-widest text-white/60">Items on this bill</div>
            <button type="button" onClick={addItem} data-testid="add-item-btn"
              className="inline-flex items-center gap-1 border border-brand-blue text-brand-blue px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest hover:bg-brand-blue/10">
              <Plus size={12} /> Add Line
            </button>
          </div>
          {items.length === 0 && (
            <div className="border border-dashed border-white/15 py-6 text-center text-white/40 text-xs">
              No items yet — click &quot;Add Line&quot; to start.
            </div>
          )}
          <div className="space-y-2">
            {items.map((it, idx) => (
              <div key={idx} className="border border-white/12 bg-black/30 p-3" data-testid={`purchase-item-${idx}`}>
                <div className="grid grid-cols-12 gap-2 items-end">
                  {/* Existing / New toggle */}
                  <div className="col-span-12 md:col-span-3">
                    <label className="block font-mono text-[9px] uppercase tracking-widest text-white/50 mb-1">Item source</label>
                    <select value={it.mode} onChange={(e) => setItem(idx, { mode: e.target.value, kit_id: "", name: e.target.value === "existing" ? "" : it.name })}
                      data-testid={`item-mode-${idx}`}
                      className="w-full bg-black/40 border border-white/15 px-2 py-2 text-xs">
                      <option value="existing">From catalog (stock ↑)</option>
                      <option value="new">New kit — add to catalog</option>
                      <option value="none">Non-stock item (no catalog)</option>
                    </select>
                  </div>

                  {/* Kit picker OR name field */}
                  {it.mode === "existing" ? (
                    <div className="col-span-12 md:col-span-4">
                      <label className="block font-mono text-[9px] uppercase tracking-widest text-white/50 mb-1">Pick kit ({kits.length})</label>
                      <select value={it.kit_id} onChange={(e) => pickKit(idx, e.target.value)}
                        data-testid={`item-kit-${idx}`}
                        className="w-full bg-black/40 border border-white/15 px-2 py-2 text-xs">
                        <option value="">— select —</option>
                        {kits.map((k) => (
                          <option key={k.id} value={k.id}>{k.name} · stock {k.stock ?? "∞"}</option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div className="col-span-12 md:col-span-4">
                      <label className="block font-mono text-[9px] uppercase tracking-widest text-white/50 mb-1">
                        Item name {it.mode === "new" && <span className="text-brand-blue">· will be added to catalog</span>}
                      </label>
                      <input value={it.name} onChange={(e) => setItem(idx, { name: e.target.value })}
                        data-testid={`item-name-${idx}`} placeholder="e.g. Kookaburra Regulation Ball"
                        className="w-full bg-black/40 border border-white/15 px-2 py-2 text-xs" />
                    </div>
                  )}

                  <div className="col-span-4 md:col-span-1">
                    <label className="block font-mono text-[9px] uppercase tracking-widest text-white/50 mb-1">Qty</label>
                    <input type="number" min="0" step="1" value={it.quantity} onChange={(e) => setItem(idx, { quantity: e.target.value })}
                      data-testid={`item-qty-${idx}`}
                      className="w-full bg-black/40 border border-white/15 px-2 py-2 text-xs" />
                  </div>
                  <div className="col-span-4 md:col-span-2">
                    <label className="block font-mono text-[9px] uppercase tracking-widest text-white/50 mb-1">Unit ₹</label>
                    <input type="number" min="0" step="0.01" value={it.unit_price} onChange={(e) => setItem(idx, { unit_price: e.target.value })}
                      data-testid={`item-price-${idx}`}
                      className="w-full bg-black/40 border border-white/15 px-2 py-2 text-xs" />
                  </div>
                  <div className="col-span-3 md:col-span-1 text-right">
                    <div className="font-mono text-[9px] uppercase tracking-widest text-white/50 mb-1">Line</div>
                    <div className="font-mono text-sm brand-blue py-2" data-testid={`item-total-${idx}`}>
                      {formatINR(Number(it.quantity || 0) * Number(it.unit_price || 0))}
                    </div>
                  </div>
                  <div className="col-span-1 flex justify-end">
                    <button type="button" onClick={() => rmItem(idx)} data-testid={`remove-item-${idx}`}
                      className="text-red-400 hover:text-red-300 pb-2"><Trash2 size={14} /></button>
                  </div>
                </div>
                {/* Per-line Category + GST% (Tally-style, overrides bill defaults) */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                  <div>
                    <label className="block font-mono text-[9px] uppercase tracking-widest text-white/50 mb-1">Category</label>
                    <select value={it.category || form.category} onChange={(e) => setItem(idx, { category: e.target.value })}
                      data-testid={`item-category-${idx}`}
                      className="w-full bg-black/40 border border-white/15 px-2 py-1.5 text-xs">
                      {["kit","consumable","repair","rent","utility","salary","food","other"].map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block font-mono text-[9px] uppercase tracking-widest text-white/50 mb-1">GST %</label>
                    <select value={it.gst_percent ?? form.gst_percent} onChange={(e) => setItem(idx, { gst_percent: e.target.value })}
                      data-testid={`item-gst-${idx}`}
                      className="w-full bg-black/40 border border-white/15 px-2 py-1.5 text-xs">
                      {[0, 5, 12, 18, 28].map((r) => <option key={r} value={r}>{r}%</option>)}
                    </select>
                  </div>
                  {it.mode === "new" && (
                    <>
                      <div>
                        <label className="block font-mono text-[9px] uppercase tracking-widest text-white/50 mb-1">SKU (opt)</label>
                        <input value={it.sku} onChange={(e) => setItem(idx, { sku: e.target.value })}
                          className="w-full bg-black/40 border border-white/15 px-2 py-1.5 text-xs" />
                      </div>
                      <div>
                        <label className="block font-mono text-[9px] uppercase tracking-widest text-white/50 mb-1">HSN (opt)</label>
                        <input value={it.hsn} onChange={(e) => setItem(idx, { hsn: e.target.value })}
                          className="w-full bg-black/40 border border-white/15 px-2 py-1.5 text-xs" />
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Totals + Payment */}
        <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4 border-b border-white/10">
          <div className="md:col-span-2 grid grid-cols-2 gap-3">
            <Field label="Payment status">
              <select value={form.payment_status} onChange={(e) => setForm({ ...form, payment_status: e.target.value })} data-testid="purchase-status" className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm">
                <option value="paid">Paid now</option>
                <option value="pending">Unpaid (creditor)</option>
              </select>
            </Field>
            <Field label="Method">
              <select value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} data-testid="purchase-method" className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm">
                {["cash","upi","card","cheque","bank_transfer"].map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </Field>
            <Field label="Notes" span={2}><input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" /></Field>
          </div>
          <div className="border border-brand-orange/40 bg-brand-orange/5 p-3 space-y-1 text-xs">
            <div className="flex justify-between text-white/60"><span>Subtotal</span><span className="font-mono" data-testid="pt-subtotal">{formatINR(subtotal)}</span></div>
            <div className="flex justify-between text-white/60"><span>GST ({form.gst_percent}%)</span><span className="font-mono brand-orange">{formatINR(gstAmt)}</span></div>
            <div className="flex justify-between font-bold pt-2 border-t border-white/20 mt-2"><span>Grand Total</span><span className="font-display text-lg brand-blue" data-testid="pt-grand">{formatINR(grandTotal)}</span></div>
          </div>
        </div>

        <div className="p-5 flex gap-2">
          <button type="button" onClick={onClose} className="flex-1 border border-white/15 py-2.5 font-mono text-xs uppercase tracking-widest">Cancel</button>
          <button type="submit" disabled={busy} data-testid="purchase-save"
            className="flex-[2] bg-brand-orange text-black py-2.5 font-bold text-xs uppercase tracking-widest disabled:opacity-50">
            {busy ? "Saving…" : `Save Purchase · ${formatINR(grandTotal)}`}
          </button>
        </div>
      </form>
      {showNewSupplier && (
        <NewSupplierModal onClose={() => setShowNewSupplier(false)} onDone={onSupplierCreated} />
      )}
    </div>
  );
}

function NewSupplierModal({ onClose, onDone }) {
  const [form, setForm] = useState({
    name: "", gstin: "", phone: "", email: "",
    address_line1: "", city: "", state: "", state_code: "", pincode: "",
    pan: "", opening_balance: 0, notes: "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!form.name.trim()) { toast.error("Supplier name required"); return; }
    setBusy(true);
    try {
      const r = await api.post("/admin/suppliers", { ...form, opening_balance: Number(form.opening_balance || 0) });
      toast.success(`Supplier "${r.data.name}" saved`);
      onDone(r.data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/85 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} data-testid="new-supplier-modal"
        className="w-full max-w-lg bg-[#131A2B] border border-brand-orange/50 my-4">
        <div className="p-4 border-b border-white/10 flex justify-between items-center">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">// Add supplier · ledger</div>
            <div className="font-display text-lg font-black uppercase mt-0.5">New Supplier</div>
          </div>
          <button type="button" onClick={onClose} className="text-white/50"><X size={16} /></button>
        </div>
        <div className="p-4 grid grid-cols-2 gap-3">
          <Field label="Name" span={2}><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="ns-name" className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" /></Field>
          <Field label="GSTIN"><input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })} placeholder="27ABCDE1234F1Z5" data-testid="ns-gstin" className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm font-mono" /></Field>
          <Field label="PAN"><input value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })} data-testid="ns-pan" className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm font-mono" /></Field>
          <Field label="Phone"><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="ns-phone" className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" /></Field>
          <Field label="Email"><input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="ns-email" className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" /></Field>
          <Field label="Address" span={2}><input value={form.address_line1} onChange={(e) => setForm({ ...form, address_line1: e.target.value })} className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" /></Field>
          <Field label="City"><input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" /></Field>
          <Field label="State"><input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" /></Field>
          <Field label="State Code"><input value={form.state_code} onChange={(e) => setForm({ ...form, state_code: e.target.value })} placeholder="27" className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm font-mono" /></Field>
          <Field label="Pincode"><input value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm font-mono" /></Field>
          <Field label="Opening balance ₹" span={2}>
            <input type="number" min="0" step="0.01" value={form.opening_balance} onChange={(e) => setForm({ ...form, opening_balance: e.target.value })}
              className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" />
          </Field>
        </div>
        <div className="p-4 border-t border-white/10 flex gap-2">
          <button type="button" onClick={onClose} className="flex-1 border border-white/15 py-2.5 font-mono text-xs uppercase tracking-widest">Cancel</button>
          <button type="submit" disabled={busy} data-testid="ns-save"
            className="flex-[2] bg-brand-orange text-black py-2.5 font-bold text-xs uppercase tracking-widest disabled:opacity-50">
            {busy ? "Saving…" : "Save Supplier & Use"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Stat({ label, value, color = "blue", raw = false, testid }) {
  const cls = { blue: "brand-blue", orange: "brand-orange", green: "text-green-400", red: "text-red-400" }[color];
  return (
    <div className="p-4 border border-white/12 bg-[#131A2B]" data-testid={testid}>
      <div className="font-mono text-[10px] uppercase tracking-widest text-white/50">{label}</div>
      <div className={`font-display text-xl font-black mt-1 ${cls}`}>{raw ? value : formatINR(value)}</div>
    </div>
  );
}
function Line({ label, value, bold, big, testid }) {
  return (
    <div className={`flex justify-between py-1.5 ${bold ? "border-t border-white/20 mt-2 pt-2 font-bold" : ""}`} data-testid={testid}>
      <span className={`text-xs ${bold ? "text-white" : "text-white/60"}`}>{label}</span>
      <span className={`font-mono ${big ? "font-display text-xl font-black brand-blue" : bold ? "brand-blue" : "text-white/80"}`}>{formatINR(value)}</span>
    </div>
  );
}
function Field({ label, span = 1, children }) {
  return (
    <div className={span === 2 ? "col-span-2" : ""}>
      <label className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1">{label}</label>
      {children}
    </div>
  );
}


// ---------- Bill Detail Modal (used from Sales + Purchase register row-click) ----------
function BillDetailModal({ kind, id, label, onClose }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/admin/ledgers/bill/${kind}/${id}`);
        setDoc(r.data);
      } catch (e) { toast.error(apiError(e)); onClose(); }
      finally { setLoading(false); }
    })();
  }, [kind, id, onClose]);

  const downloadPdf = () => {
    const path = kind === "purchase"
      ? `/api/admin/purchases/${id}/pdf`
      : kind === "invoice"
        ? `/api/parent/invoices/${id}/pdf`
        : null;
    if (!path) { toast.error("Booking PDFs not supported yet"); return; }
    const url = `${process.env.REACT_APP_BACKEND_URL}${path}`;
    window.open(url, "_blank");
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center p-2 md:p-4 overflow-y-auto" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="bill-detail-modal"
        className="w-full max-w-3xl bg-[#131A2B] border border-brand-blue/40 my-4">
        <div className="p-5 border-b border-white/10 flex justify-between items-start gap-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest brand-blue">// {kind.toUpperCase()} DETAIL</div>
            <div className="font-display text-xl font-black uppercase mt-0.5">{label || "—"}</div>
            {doc && <div className="text-[10px] font-mono text-white/50 mt-1">
              {kind === "purchase" ? doc.bill_no : (doc.invoice_number || doc.qr_token || "")} · {(doc.date || doc.created_at || "").slice(0, 10)}
            </div>}
          </div>
          <div className="flex gap-2">
            <button onClick={downloadPdf} data-testid="bill-pdf-btn"
              className="inline-flex items-center gap-1 bg-brand-orange text-black px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest">
              <Download size={12} /> PDF
            </button>
            <button onClick={onClose} className="text-white/50"><X size={16} /></button>
          </div>
        </div>
        {loading && <div className="p-8 text-center text-white/40">Loading…</div>}
        {!loading && doc && <BillBody doc={doc} kind={kind} />}
      </div>
    </div>
  );
}

function BillBody({ doc, kind }) {
  const items = doc.items || doc.kits || [];
  return (
    <div className="p-5 space-y-4">
      <div className="grid grid-cols-2 gap-3 text-xs">
        <div>
          <div className="font-mono text-[9px] uppercase tracking-widest text-white/50">{kind === "purchase" ? "Supplier" : "Party"}</div>
          <div className="mt-0.5">{doc.supplier || doc.parent_name || doc.customer_name || doc.guest_name || "—"}</div>
          {(doc.customer_phone || doc.guest_phone) && <div className="text-white/60">{doc.customer_phone || doc.guest_phone}</div>}
        </div>
        <div className="text-right">
          <div className="font-mono text-[9px] uppercase tracking-widest text-white/50">Status</div>
          <div className={`mt-0.5 font-bold uppercase tracking-widest ${doc.payment_status === "paid" ? "text-green-400" : doc.payment_status === "partial" ? "text-yellow-400" : "text-red-400"}`}>
            {doc.payment_status || "—"}
          </div>
        </div>
      </div>
      {items.length > 0 && (
        <table className="w-full text-xs border border-white/10">
          <thead className="text-[9px] font-mono uppercase tracking-widest text-white/50 border-b border-white/10">
            <tr>
              <th className="text-left px-3 py-1.5">Item</th>
              <th className="text-right px-3 py-1.5">Qty</th>
              <th className="text-right px-3 py-1.5">Rate</th>
              <th className="text-right px-3 py-1.5">GST%</th>
              <th className="text-right px-3 py-1.5">Amount</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={i} className="border-b border-white/6">
                <td className="px-3 py-1.5">{it.name}</td>
                <td className="px-3 py-1.5 text-right font-mono">{it.quantity || it.qty || 1}</td>
                <td className="px-3 py-1.5 text-right font-mono">{formatINR(it.unit_price || it.price || 0)}</td>
                <td className="px-3 py-1.5 text-right font-mono">{(it.gst_percent ?? doc.gst_percent ?? 0)}%</td>
                <td className="px-3 py-1.5 text-right font-mono brand-blue">{formatINR(it.line_total || (it.quantity || 1) * (it.unit_price || 0))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div className="text-[10px] font-mono text-white/50 space-y-0.5">
          <div>Notes: {doc.notes || doc.description || "—"}</div>
          {doc.collections && doc.collections.length > 0 && (
            <div className="mt-2">
              <div className="uppercase tracking-widest text-brand-orange mb-1">Receipts</div>
              {doc.collections.map((c, i) => (
                <div key={i}>{(c.at || "").slice(0, 16)} · {c.method} · {formatINR(c.amount)}</div>
              ))}
            </div>
          )}
          {doc.payments && doc.payments.length > 0 && (
            <div className="mt-2">
              <div className="uppercase tracking-widest text-brand-orange mb-1">Payments</div>
              {doc.payments.map((c, i) => (
                <div key={i}>{(c.at || "").slice(0, 16)} · {c.method} · {formatINR(c.amount)}</div>
              ))}
            </div>
          )}
        </div>
        <div className="border border-brand-orange/40 bg-brand-orange/5 p-3 space-y-1 text-xs">
          <div className="flex justify-between text-white/70"><span>Subtotal</span><span className="font-mono">{formatINR(doc.amount)}</span></div>
          <div className="flex justify-between text-white/70"><span>GST</span><span className="font-mono brand-orange">{formatINR(doc.tax_amount)}</span></div>
          <div className="flex justify-between font-bold pt-2 border-t border-white/20 mt-2"><span>Grand Total</span><span className="font-display text-lg brand-blue">{formatINR(doc.total)}</span></div>
          {(doc.amount_paid > 0 || doc.balance_due > 0) && (
            <>
              <div className="flex justify-between text-green-400 text-[11px]"><span>Paid</span><span className="font-mono">{formatINR(doc.amount_paid)}</span></div>
              <div className="flex justify-between text-red-400 text-[11px]"><span>Balance</span><span className="font-mono">{formatINR(doc.balance_due)}</span></div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Party Statement Modal (Tally-style ledger for one party) ----------
function PartyStatementModal({ kind, partyKey, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState({ from_: `${new Date().getFullYear()}-01-01`, to: today() });
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/ledgers/party-statement`, {
        params: { kind, key: partyKey, from_: range.from_, to: range.to },
      });
      setData(r.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind, partyKey, range]);

  const downloadPdf = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/ledgers/party-statement/pdf?kind=${kind}&key=${encodeURIComponent(partyKey)}&from_=${range.from_}&to=${range.to}`;
    window.open(url, "_blank");
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center p-2 md:p-4 overflow-y-auto" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="party-statement-modal"
        className="w-full max-w-5xl bg-[#131A2B] border border-brand-blue/40 my-4">
        <div className="p-5 border-b border-white/10 flex justify-between items-start gap-4 flex-wrap">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest brand-blue">// TALLY-STYLE LEDGER · {kind.toUpperCase()}</div>
            <div className="font-display text-xl font-black uppercase mt-0.5">{data?.party_label || partyKey}</div>
            <div className="text-[10px] font-mono text-white/50 mt-1">
              Period: {range.from_} → {range.to}
              {data && <> · Opening {formatINR(data.opening_balance)} · Closing <span className="brand-blue">{formatINR(data.closing_balance)}</span></>}
            </div>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <input type="date" value={range.from_} onChange={(e) => setRange({ ...range, from_: e.target.value })}
              className="bg-black/40 border border-white/15 px-2 py-1 text-xs font-mono" data-testid="stmt-from" />
            <input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })}
              className="bg-black/40 border border-white/15 px-2 py-1 text-xs font-mono" data-testid="stmt-to" />
            <button onClick={downloadPdf} data-testid="stmt-pdf-btn"
              className="inline-flex items-center gap-1 bg-brand-orange text-black px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest">
              <Download size={12} /> PDF
            </button>
            <button onClick={onClose} className="text-white/50"><X size={16} /></button>
          </div>
        </div>

        {loading && <div className="p-8 text-center text-white/40">Loading…</div>}
        {!loading && data && (
          <div className="p-5">
            <table className="w-full text-xs border border-white/12" data-testid="stmt-table">
              <thead className="text-[9px] font-mono uppercase tracking-widest text-white/50 border-b border-white/10">
                <tr>
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-left px-3 py-2">Particulars</th>
                  <th className="text-left px-3 py-2">Voucher</th>
                  <th className="text-left px-3 py-2">Reference</th>
                  <th className="text-right px-3 py-2">Debit</th>
                  <th className="text-right px-3 py-2">Credit</th>
                  <th className="text-right px-3 py-2">Balance</th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-brand-orange/10 border-b border-white/10">
                  <td className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-white/60">{range.from_}</td>
                  <td className="px-3 py-2 font-bold uppercase tracking-widest brand-orange">Opening Balance</td>
                  <td className="px-3 py-2" colSpan="3"></td>
                  <td className="px-3 py-2 text-right font-mono brand-orange">
                    {kind === "debtor"
                      ? (data.opening_balance >= 0 ? formatINR(data.opening_balance) : "—")
                      : (data.opening_balance <= 0 ? formatINR(-data.opening_balance) : "—")}
                  </td>
                  <td className="px-3 py-2 text-right font-mono brand-orange">{formatINR(data.opening_balance)}</td>
                </tr>
                {data.rows.length === 0 && <tr><td colSpan="7" className="text-center py-8 text-white/40">No transactions in range.</td></tr>}
                {data.rows.map((r, i) => (
                  <tr key={i} className="border-b border-white/6 hover:bg-white/5" data-testid={`stmt-row-${i}`}>
                    <td className="px-3 py-2 font-mono text-[10px]">{r.date}</td>
                    <td className="px-3 py-2">{r.particulars}</td>
                    <td className="px-3 py-2 text-[10px] font-mono uppercase tracking-widest brand-orange">{r.voucher_type}</td>
                    <td className="px-3 py-2 font-mono text-[10px] text-white/50">{r.reference || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono text-green-400">{r.debit > 0 ? formatINR(r.debit) : "—"}</td>
                    <td className="px-3 py-2 text-right font-mono text-red-400">{r.credit > 0 ? formatINR(r.credit) : "—"}</td>
                    <td className="px-3 py-2 text-right font-mono brand-blue">{formatINR(r.balance)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t-2 border-white/20 font-bold">
                <tr>
                  <td className="px-3 py-2 uppercase tracking-widest font-mono text-[10px]" colSpan="4">TOTAL</td>
                  <td className="px-3 py-2 text-right font-mono text-green-400">{formatINR(data.total_debit)}</td>
                  <td className="px-3 py-2 text-right font-mono text-red-400">{formatINR(data.total_credit)}</td>
                  <td className="px-3 py-2 text-right font-mono brand-blue">{formatINR(data.closing_balance)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
